import { colors } from '@kmns-internal/furikake'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Divider from '@mui/material/Divider'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import {
  ResponseTypeEnum,
  TemplateQuestion,
} from '@ulysses-inc/harami_api_client'
import { useState } from 'react'
import { MultipleChoiceSectionWithTabs } from 'src/features/templateEdit/itemAppender/ResponseDropDown/components/MultipleChoiceSectionWithTabs/MultipleChoiceSectionWithTabs'
import { useMultipleChoiceDrawer } from 'src/features/templateEdit/itemAppender/ResponseDropDown/hooks/useMultipleChoiceDrawer'
import { DisabledTableCellBackgroundColor } from 'src/features/theme/KdsThemeColor'
import {
  type ColumnInputType,
  getColumnTypeUi,
  isColumnInputType,
} from '../model'
import { InputFormTypePalette } from './InputFormTypePalette'

type Props = {
  responseType?: ResponseTypeEnum
  /** 変更不可の理由(条件分岐が紐づいている列など)。指定するとグレー表示で開けなくなり、ホバーで理由を表示する。 */
  disabledReason?: string
  onChangeType: (responseType: ColumnInputType) => void
  onUpdateColumn: (patch: Partial<TemplateQuestion>) => void
}

/**
 * 選択肢ドロワーの z-index。回答形式の Popover(MUI Modal: 既定 1300) の上に出す必要があるため、
 * それより大きい値にする。これがないと Popover 内の入力フォームパレットがドロワーの前面に残る。
 */
const CHOICES_DRAWER_Z_INDEX = 1400

/**
 * 回答形式の選択 UI。既存の質問編集 UI と同様に、タップで「選択肢パネル + 入力フォーム」を
 * ポップオーバー表示する。選択肢/セット選択肢は選択肢パネルから選ぶと型が切り替わる。
 */
export const TableColumnResponseTypeSelect = ({
  responseType,
  disabledReason,
  onChangeType,
  onUpdateColumn,
}: Props) => {
  const isDisabled = disabledReason !== undefined
  const choicesDrawer = useMultipleChoiceDrawer({
    zIndex: CHOICES_DRAWER_Z_INDEX,
  })
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const close = () => setAnchorEl(null)

  const currentTypeUi =
    responseType != null && isColumnInputType(responseType)
      ? getColumnTypeUi(responseType)
      : undefined
  const CurrentIcon = currentTypeUi?.Icon

  return (
    <Stack spacing={0.75} sx={{ flexShrink: 0 }}>
      <Typography variant="body2" fontWeight={700}>
        回答形式
      </Typography>
      {/* disabled な要素はホバーイベントを受けられないため、span で一段ラップして Tooltip を効かせる */}
      <Tooltip placement="top" title={disabledReason ?? ''}>
        <span>
          <ButtonBase
            disabled={isDisabled}
            onClick={event => setAnchorEl(event.currentTarget)}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl)}
            sx={{
              width: 280,
              justifyContent: 'space-between',
              gap: 1,
              px: 1.5,
              py: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: isDisabled
                ? DisabledTableCellBackgroundColor
                : colors.white,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              {CurrentIcon && (
                <Box
                  sx={{
                    flexShrink: 0,
                    display: 'inline-flex',
                    width: 24,
                    height: 24,
                  }}
                >
                  <CurrentIcon width={24} height={24} />
                </Box>
              )}
              <Typography variant="body2" noWrap>
                {currentTypeUi?.label ?? '未選択'}
              </Typography>
            </Stack>
            <ExpandMoreIcon fontSize="small" color="action" />
          </ButtonBase>
        </span>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        // Popover の外にある選択肢ドロワーで入力できるよう、フォーカストラップを無効にする。
        // (有効だと Popover が内側へフォーカスを戻し続け、ドロワー内フォームを操作できない)
        disableEnforceFocus
        // 選択肢ドロワーを開いている間は閉じない(既存 ResponseDropDown と同じ挙動)。
        onClose={() => {
          if (choicesDrawer.isVisible) return
          close()
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Stack direction="row" alignItems="stretch">
          {/* 選択肢パネル(MultipleChoiceSectionWithTabs が「選択肢」見出しを内包する)。 */}
          <Box sx={{ flexShrink: 0, p: 1 }}>
            <MultipleChoiceSectionWithTabs
              openMultipleChoiceDrawer={choicesDrawer.open}
              onCloseDropdown={close}
              dispatchUpdateTemplateNode={onUpdateColumn}
            />
          </Box>

          <Divider orientation="vertical" flexItem />

          <InputFormTypePalette
            selectedType={responseType}
            onSelect={type => {
              onChangeType(type)
              close()
            }}
          />
        </Stack>
      </Popover>

      {choicesDrawer.renderDrawer()}
    </Stack>
  )
}
